import { useState, useEffect } from 'react';
import { X, FileMusic, Loader2, Trash2, Plus, Search } from 'lucide-react';
import { getDjLocalFiles, deleteDjLocalFile, addLocalFileToQueue, type DjLocalFile } from '../../services/localFiles';
import toast from 'react-hot-toast';

interface Props {
  eventId: string;
  djId: string;
  onClose: () => void;
  onAdded: () => void;
}

export default function DjLocalLibraryModal({ eventId, djId, onClose, onAdded }: Props) {
  const [files, setFiles] = useState<DjLocalFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getDjLocalFiles(djId);
        setFiles(result);
      } catch {
        toast.error('Failed to load library');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [djId]);

  const filtered = files.filter(f => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return f.title.toLowerCase().includes(q) || f.artist.toLowerCase().includes(q) || f.fileName.toLowerCase().includes(q);
  });

  const handleAdd = async (file: DjLocalFile) => {
    setAddingId(file.id);
    try {
      await addLocalFileToQueue(eventId, djId, {
        storageUrl: file.storageUrl,
        fileName: file.fileName,
        title: file.title,
        artist: file.artist,
        durationMs: file.durationMs,
        temporary: false,
      }, {
        visibleToAttendees: true,
        includeInPlaylist: true,
      });
      toast.success(`"${file.title}" added to queue`);
      onAdded();
    } catch {
      toast.error('Failed to add to queue');
    } finally {
      setAddingId(null);
    }
  };

  const handleDelete = async (file: DjLocalFile) => {
    setDeletingId(file.id);
    try {
      await deleteDjLocalFile(djId, file.id, file.storageUrl);
      setFiles(prev => prev.filter(f => f.id !== file.id));
      toast.success('File deleted from library');
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDuration = (ms: number) => {
    if (!ms) return '--:--';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl p-5 shadow-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FileMusic className="w-5 h-5 text-primary" />
            My Library
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-circle">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        {files.length > 3 && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search library..."
              className="input input-bordered input-sm w-full pl-9"
            />
          </div>
        )}

        {/* File List */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileMusic className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>{files.length === 0 ? 'No permanent files yet' : 'No matches'}</p>
            </div>
          ) : (
            filtered.map(file => (
              <div key={file.id} className="flex items-center gap-3 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{file.title}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                    {file.artist || 'Unknown artist'} &bull; {formatDuration(file.durationMs)}
                  </p>
                </div>
                <button
                  onClick={() => handleAdd(file)}
                  disabled={addingId === file.id}
                  className="btn btn-sm btn-primary btn-circle"
                  title="Add to queue"
                >
                  {addingId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => handleDelete(file)}
                  disabled={deletingId === file.id}
                  className="btn btn-sm btn-ghost btn-circle text-danger"
                  title="Delete from library"
                >
                  {deletingId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))
          )}
        </div>

        <div className="pt-3 border-t border-gray-300 dark:border-gray-600 mt-3">
          <button onClick={onClose} className="btn btn-ghost w-full">Close</button>
        </div>
      </div>
    </>
  );
}
